<script setup lang="ts">
import { computed, nextTick, onBeforeUnmount, onMounted, ref, watch } from 'vue'
import { Coins, Droplets, Heart, Sparkles } from 'lucide-vue-next'
import type { AppLocale } from '@/i18n/messages'
import { streamMessages } from '@/i18n/stream'
import { isAdventureSkin, isSceneSkin, resolvePipBackground, type PipStreamOptions, type StreamSkin } from '@/ui/pip-skins'
import AdventureComposition from './AdventureComposition.vue'
import MushroomComposition from './MushroomComposition.vue'
import SceneComposition from './SceneComposition.vue'
import { fitStreamDetail, resolveStreamShape, STREAM_ASSETS, type StreamHudData } from '@/ui/stream-hud'
import { resetPipScale, fitPipScale } from '@/ui/pip-scale'
import { streamContentFits } from '@/ui/stream-hud'
import { ExpRateMotionTracker } from '@/ui/stream-motion'

const props = defineProps<{ skin: StreamSkin; data: StreamHudData; locale: AppLocale; options: PipStreamOptions; preview?: boolean }>()
const root = ref<HTMLElement>()
const copy = computed(() => streamMessages[props.locale])
const failed = ref(new Set<string>())
const assets = Object.fromEntries(Object.entries(STREAM_ASSETS).map(([key, path]) => [key, new URL(path, window.location.href).href])) as typeof STREAM_ASSETS
const imageFailed = (name: string) => { failed.value = new Set([...failed.value, name]) }
let observer: ResizeObserver | undefined
let frame: number | undefined
let owner: Window | null = null
let disposed = false
const rateMotion = new ExpRateMotionTracker()
let rateAnimations: Animation[] = []

watch(() => [props.data, props.skin] as const, ([data, skin], previous) => {
  if (previous && (previous[1] !== skin || previous[0].sessionId !== data.sessionId || data.status !== 'running')) {
    rateAnimations.forEach((animation) => animation.cancel())
    rateAnimations = []
  }
  const direction = rateMotion.observe(data.expRateValue, Date.now(), `${data.sessionId}:${skin}`, data.status === 'running')
  if (!direction || isAdventureSkin(skin) || isSceneSkin(skin) || skin === 'stream-mushroom') return
  rateAnimations.forEach((animation) => animation.cancel())
  rateAnimations = []
  const animate = (selector: string, middle: string) => {
    const animation = root.value?.querySelector<HTMLElement>(selector)?.animate?.(
      [{ transform: 'translateY(0) scale(1) rotate(0)' }, { transform: middle }, { transform: 'translateY(0) scale(1) rotate(0)' }],
      { duration: 560, easing: 'ease-out' },
    )
    if (animation) rateAnimations.push(animation)
  }
  animate('.sh-exp-value', direction === 'rise' ? 'translateY(-.1875rem) scale(1.04)' : 'translateY(.125rem) scale(.97)')
}, { immediate: true })

function layout() {
  frame = undefined
  const node = root.value
  if (!node || disposed) return
  resetPipScale(node)
  // client sizes remain in CSS pixels when the settings preview is scaled.
  const width = node.clientWidth
  const height = node.clientHeight
  if (!width || !height) return
  node.dataset.shape = resolveStreamShape(width, height)
  fitStreamDetail(node)
  fitPipScale(node, streamContentFits)
}
function scheduleLayout() {
  if (disposed || frame !== undefined) return
  if (owner?.requestAnimationFrame) frame = owner.requestAnimationFrame(layout)
  else layout()
}

watch(() => [props.data, props.skin, props.locale], () => { void nextTick(scheduleLayout) })
// Watch only confirmed event IDs. Initial mount and skin/session changes establish a baseline.
watch(() => [props.data.levelUpId, props.skin, props.data.sessionId] as const, ([id, skin, session], [oldId, oldSkin, oldSession]) => {
  if (!id || id === oldId || skin !== oldSkin || session !== oldSession || props.preview || props.data.status !== 'running' || isAdventureSkin(skin) || isSceneSkin(skin) || skin === 'stream-mushroom') return
  const target = root.value?.querySelector<HTMLElement>('.sh-level')
  target?.getAnimations?.().forEach((animation) => animation.cancel())
  target?.animate?.([{ transform: 'scale(1)' }, { transform: 'scale(1.08)' }, { transform: 'scale(1)' }], { duration: 520, easing: 'ease-out' })
})
onMounted(() => {
  owner = root.value?.ownerDocument.defaultView ?? window
  if (typeof ResizeObserver !== 'undefined') {
    observer = new ResizeObserver(scheduleLayout)
    // Observe the viewport, not the canvas enlarged by the tiny-window fallback.
    if (root.value?.parentElement) observer.observe(root.value.parentElement)
  }
  owner.addEventListener('resize', scheduleLayout)
  root.value?.addEventListener('load', scheduleLayout, true)
  void root.value?.ownerDocument.fonts?.ready.then(scheduleLayout)
  layout()
})
onBeforeUnmount(() => {
  disposed = true
  rateAnimations.forEach((animation) => animation.cancel())
  observer?.disconnect()
  owner?.removeEventListener('resize', scheduleLayout)
  root.value?.removeEventListener('load', scheduleLayout, true)
  if (frame !== undefined) owner?.cancelAnimationFrame(frame)
})
</script>

<template>
  <main ref="root" class="stream-hud" :data-stream-skin="skin" :data-status="data.status"
    :data-only-exp="!data.enabled.mesos" data-detail="5" data-shape="classic" :lang="locale"
    :style="{ '--stream-canvas': preview ? 'transparent' : resolvePipBackground(options.background) }" :aria-label="copy.title">
    <div v-if="data.status !== 'running'" class="sh-alert" role="status" aria-atomic="true" data-stream-measure><i />{{ data.statusText }}</div>
    <AdventureComposition v-if="isAdventureSkin(skin)" :key="skin" :skin="skin" :data="data" :locale="locale" :preview="preview" />
    <MushroomComposition v-else-if="skin === 'stream-mushroom'" :data="data" :locale="locale" :preview="preview" />
    <SceneComposition v-else-if="isSceneSkin(skin)" :key="skin" :skin="skin" :data="data" :locale="locale" :preview="preview" />
    <div v-else class="sh-composition" data-stream-measure>
      <div class="sh-level" data-tier="1" data-stream-measure><small>Lv.</small><strong data-stream-measure>{{ data.level }}</strong></div>
      <section class="sh-exp sh-plaque" :aria-label="copy.exp" data-stream-measure>
        <div class="sh-kicker" data-stream-measure><span class="sh-icon" aria-hidden="true"><img v-if="!failed.has('exp')" :src="assets.exp" alt="" @error="imageFailed('exp')"><Sparkles v-else /></span><span data-stream-measure>{{ copy.exp }}</span></div>
        <strong class="sh-value sh-exp-value" data-stream-exp data-stream-measure>{{ data.expRate }}</strong>
        <div class="sh-progress-group" data-tier="1" data-stream-measure>
          <div class="sh-progress" :aria-label="`EXP ${data.expPercent}`"><i :style="{ transform: `scaleX(${data.expRatio})` }" /></div>
          <div class="sh-progress-copy" data-stream-measure><strong data-stream-measure>{{ data.expPercent }}</strong><span data-stream-measure>{{ copy.eta }} <b>{{ data.eta }}</b></span></div>
        </div>
        <div class="sh-fact" data-tier="3" data-stream-measure><span>{{ data.expGainedLabel }}</span><b data-stream-measure>{{ data.expGained }}</b></div>
        <div class="sh-fact" data-tier="5" data-stream-measure><span>{{ copy.full }}</span><b data-stream-measure>{{ data.fullDuration }}</b></div>
      </section>
      <section v-if="data.enabled.mesos" class="sh-mesos sh-plaque" data-tier="2" :aria-label="copy.mesos" data-stream-measure>
        <div class="sh-kicker" data-stream-measure><span class="sh-icon" aria-hidden="true"><img v-if="!failed.has('mesos')" :src="assets.mesos" alt="" @error="imageFailed('mesos')"><Coins v-else /></span><span data-stream-measure>{{ copy.mesos }}</span></div>
        <strong class="sh-value" data-stream-mesos data-stream-measure>{{ data.mesosRate }}</strong>
        <div class="sh-fact" data-tier="3" data-stream-measure><span>{{ copy.net }}</span><b data-stream-measure>{{ data.mesosNet }}</b></div>
        <div class="sh-fact" data-tier="5" data-stream-measure><span>{{ copy.start }}</span><b data-stream-measure>{{ data.mesosStart }}</b></div>
        <div class="sh-fact" data-tier="5" data-stream-measure><span>{{ copy.balance }}</span><b data-stream-measure>{{ data.mesosBalanceChange }}</b></div>
      </section>
    </div>
    <div v-if="data.enabled.hp || data.enabled.mp" class="sh-resources" data-tier="4" data-stream-measure>
      <div v-for="key in (['hp', 'mp'] as const)" v-show="data.enabled[key]" :key="key" class="sh-resource" :data-resource="key" data-stream-measure>
        <span class="sh-icon" aria-hidden="true"><img v-if="!failed.has(key)" :src="assets[key]" alt="" @error="imageFailed(key)"><Heart v-else-if="key === 'hp'" /><Droplets v-else /></span>
        <span>{{ key.toUpperCase() }}</span><strong>{{ key === 'hp' ? data.hpPercent : data.mpPercent }}</strong>
        <div class="sh-progress"><i :style="{ transform: `scaleX(${key === 'hp' ? data.hpRatio : data.mpRatio})` }" /></div>
        <small data-stream-measure>{{ copy.consumption }} <b>{{ key === 'hp' ? data.hpRate : data.mpRate }}</b></small>
      </div>
    </div>
    <footer class="sh-footer" data-tier="3" data-stream-measure>
      <span class="sh-time" data-stream-measure>{{ copy.elapsed }} <b>{{ data.elapsed }}</b></span>
      <span v-if="data.status === 'running'" class="sh-recording" data-stream-measure><i />{{ data.statusText }}</span>
      <span v-if="data.enabled.mapName" class="sh-map" data-tier="5" data-stream-measure>{{ data.map }}</span>
    </footer>
  </main>
</template>
